import { useEffect, useState } from "react"

import type { Annotation } from "@/types"
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@coval/ui"
import { Users } from "lucide-react"

import { cn } from "@/utils/cn"

interface NumericalHumanFeedbackButtonProps {
  annotation: Annotation | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  onExplanationChange: (explanation: string | null) => void
  otherUserAnnotations?: Annotation[]
  minValue: number
  maxValue: number
  readOnly?: boolean
  displayValue?: string | null
}

export const NumericalHumanFeedbackButton = ({
  annotation,
  onValueChange,
  onExplanationChange: _onExplanationChange,
  otherUserAnnotations,
  minValue,
  maxValue,
  readOnly = false,
  displayValue,
}: NumericalHumanFeedbackButtonProps) => {
  const currentValue = annotation?.ground_truth_float_value ?? null
  const [inputValue, setInputValue] = useState(
    currentValue !== null && currentValue !== undefined
      ? String(currentValue)
      : "",
  )

  useEffect(() => {
    setInputValue(
      currentValue !== null && currentValue !== undefined
        ? String(currentValue)
        : "",
    )
  }, [currentValue])

  const handleInputBlur = () => {
    if (readOnly) {
      return
    }
    const numValue = inputValue.trim() === "" ? null : Number(inputValue)

    if (inputValue.trim() === "") {
      onValueChange(null, null, null)
    } else if (
      !isNaN(numValue!) &&
      Number.isInteger(numValue!) &&
      numValue! >= minValue &&
      numValue! <= maxValue
    ) {
      onValueChange(numValue, null, null)
    } else {
      setInputValue(
        currentValue !== null && currentValue !== undefined
          ? String(currentValue)
          : "",
      )
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (readOnly) {
      return
    }
    if (e.key === "Enter") {
      e.currentTarget.blur()
    }
  }

  const hasOtherAnnotations =
    otherUserAnnotations && otherUserAnnotations.length > 0

  const resolvedDisplayValue =
    displayValue !== undefined ? (displayValue ?? "") : inputValue

  return (
    <div className="flex flex-col items-start justify-start gap-3 w-full">
      <Label>Human Review</Label>
      <div className="flex items-start justify-start gap-1.5 w-full">
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            value={readOnly ? resolvedDisplayValue : inputValue}
            onChange={
              readOnly ? undefined : (e) => setInputValue(e.target.value)
            }
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            readOnly={readOnly}
            placeholder={readOnly ? "" : `${minValue}-${maxValue}`}
            aria-label={
              readOnly
                ? "Computed score"
                : `Enter value between ${minValue} and ${maxValue}`
            }
            className={cn("w-12", readOnly && "cursor-default")}
          />
        </div>

        {!readOnly && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                disabled={!hasOtherAnnotations}
                aria-label="View other user annotations"
                className={cn(
                  "transition-all duration-200",
                  !hasOtherAnnotations && "opacity-40",
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Users />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-64 p-2">
              <div className="max-h-48 overflow-y-auto">
                {otherUserAnnotations?.map((otherAnnotation) => {
                  const value = otherAnnotation.ground_truth_float_value
                  return (
                    <div
                      key={otherAnnotation.id}
                      className="flex items-center justify-between py-1 px-1"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-coval-text">
                          {value !== null && value !== undefined
                            ? value
                            : "N/A"}
                        </span>
                        <span className="text-xs text-coval-text-muted">
                          {otherAnnotation.assignee}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  )
}
